import { gql, type TypedDocumentNode } from "@apollo/client";
import { useLazyQuery, useMutation, useQuery } from "@apollo/client/react";
import { type GQL } from "@binale-tech/shared";

const bankingBankFinAPIWebFormInitMutation: TypedDocumentNode<
    { bankingBankFinAPIWebFormInit: GQL.IFinApiWebFormResponse },
    GQL.IMutationBankingBankFinApiWebFormInitArgs
> = gql`
    mutation bankingBankFinAPIWebFormInit($input: FinAPIBankInput!) {
        bankingBankFinAPIWebFormInit(input: $input) {
            id
            url
            createdAt
            expiresAt
            type
            status
        }
    }
`;

const bankingBankFinAPIWebFormFinalizeMutation: TypedDocumentNode<
    { bankingBankFinAPIWebFormFinalize: GQL.IFinApiBankWebFormFinalizeResponse },
    GQL.IMutationBankingBankFinApiWebFormFinalizeArgs
> = gql`
    mutation bankingBankFinAPIWebFormFinalize($input: FinAPIWebFormFinalizeInput!) {
        bankingBankFinAPIWebFormFinalize(input: $input) {
            id
            error
            success
            connection {
                id
                bank {
                    id
                    name
                    location
                    city
                    logo {
                        id
                        url
                    }
                }
                accounts {
                    id
                    iban
                    accountType
                    accountHolderName
                    accountName
                    balance
                    accountCurrency
                }
            }
        }
    }
`;

const bankingBankFinAPITransactionsImportMutation: TypedDocumentNode<
    { bankingBankFinAPITransactionsImport: GQL.IFinApiTransactionsImportResponse },
    GQL.IMutationBankingBankFinApiTransactionsImportArgs
> = gql`
    mutation bankingBankFinAPITransactionsImport($input: FinAPITransactionsImportInput!) {
        bankingBankFinAPITransactionsImport(input: $input) {
            id
            success
            error
            transactionsCount
            transactionsDateFrom
            transactionsDateTo
        }
    }
`;

const bankingBankFinAPIConnectionUpdateMutation: TypedDocumentNode<
    { bankingBankFinAPIConnectionUpdate: GQL.IFinApiConnectionUpdateResponse },
    GQL.IMutationBankingBankFinApiConnectionUpdateArgs
> = gql`
    mutation bankingBankFinAPIConnectionUpdate($input: FinAPIBankInput!) {
        bankingBankFinAPIConnectionUpdate(input: $input) {
            id
            success
            webForm {
                id
                url
                createdAt
                expiresAt
                type
                status
            }
        }
    }
`;

const bankingBankConnectionsQuery: TypedDocumentNode<
    { bankingBankConnections: GQL.IFinApiBankConnection[] },
    GQL.IQueryBankingBankConnectionsArgs
> = gql`
    query bankingBankConnections($companyId: ID!) {
        bankingBankConnections(companyId: $companyId) {
            id
            bank {
                id
                name
                location
                city
                logo {
                    id
                    url
                }
            }
            accounts {
                id
                iban
                accountType
                accountHolderName
                accountName
                balance
                accountCurrency
            }
        }
    }
`;

export const useFinApiWebFormInit = () => useMutation(bankingBankFinAPIWebFormInitMutation);

export const useFinApiWebFormFinalize = () => useMutation(bankingBankFinAPIWebFormFinalizeMutation);

export const useFinApiTransactionsImport = () => useMutation(bankingBankFinAPITransactionsImportMutation);

export const useFinApiConnectionUpdate = () => useMutation(bankingBankFinAPIConnectionUpdateMutation);

export const useFinApiBankConnectionsLazy = () =>
    useLazyQuery(bankingBankConnectionsQuery, { fetchPolicy: "no-cache" });

export const useFinApiBankConnections = (companyId: string) =>
    useQuery(bankingBankConnectionsQuery, { variables: { companyId }, fetchPolicy: "no-cache", skip: !companyId });

const bankingBankSetFinAPIConnectionMutation: TypedDocumentNode<
    { bankingBankSetFinAPIConnection: boolean },
    GQL.IMutationBankingBankSetFinApiConnectionArgs
> = gql`
    mutation bankingBankSetFinAPIConnection($id: ID!, $companyId: ID!, $input: FinAPIConnection!) {
        bankingBankSetFinAPIConnection(id: $id, companyId: $companyId, input: $input)
    }
`;

export const useFinApiBankConnectionSet = () => useMutation(bankingBankSetFinAPIConnectionMutation);
